import { createMemo, For, Show } from "solid-js";
import { useLocation } from "@tanstack/solid-router";
import { Link } from "@/lib/u-router";
import { cn, isAdminCapable } from "@comandero/shared";
import {
  BarChart3,
  Boxes,
  Bug,
  CalendarClock,
  ChefHat,
  ChevronsUpDown,
  Contact,
  CreditCard,
  Flag,
  Heart,
  History as HistoryIcon,
  LayoutDashboard,
  LayoutGrid,
  LogOut,
  Mail,
  Palette,
  Plug,
  Scan,
  Shield,
  ShieldAlert,
  ShoppingCart,
  Trash2,
  Trophy,
  Truck,
  Type,
  User,
  UserCircle,
  Users,
  UtensilsCrossed,
} from "lucide-solid";

import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Logo } from "@/components/ui/logo";
import { SidebarNavItem } from "@/components/ui/sidebar-nav-item";
import { safeRedirectTarget, useSession } from "@/lib/auth";
import { readActiveAccountSync } from "@/lib/accounts";
import { useAccountSwitcher } from "@/features/auth/use-account-switcher";
import { useI18n } from "@/providers/i18n-provider";
import { useSignOut } from "@/providers/boot-manager";
import { msg } from "@lingui/core/macro";

const inactiveClass =
  "text-muted-foreground/80 hover:bg-accent/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:bg-accent/5 transition-[background-color,color,box-shadow]";

interface AppSidebarProps {
  onCloseSidebar?: () => void;
}

export function AppSidebar(props: AppSidebarProps) {
  const session = useSession();
  const location = useLocation();
  const { t } = useI18n();
  const { promptSignOut } = useSignOut();
  const accountSwitcher = useAccountSwitcher();

  // Reactive view of the active account for the switcher trigger label. Reading
  // `session.data` registers the dependency so this recomputes when the session
  // changes (switch/sign-in re-primes it).
  const activeAccount = createMemo(() => {
    void session.data;
    return readActiveAccountSync();
  });

  const activeLabel = () => {
    const a = activeAccount();
    if (a?.kind === "real") {
      return a.displayName;
    }
    return t(msg`Guest`);
  };

  const activeInitial = () => {
    const a = activeAccount();
    return (a?.displayName ?? a?.email ?? "?").charAt(0);
  };

  // Guardamos el descriptor `msg` (NO el string traducido): `t()` se llama en el
  // render (`label={t(item.label)}`) para que sea REACTIVO al cambiar idioma.
  // Llamar `t()` aquí lo evaluaría una sola vez y el sidebar no cambiaría de idioma.
  const NAV_ITEMS = [
    { to: "/", label: msg`Dashboard`, icon: LayoutDashboard },
    { to: "/pos", label: msg`Point of sale`, icon: ShoppingCart },
    { to: "/kds", label: msg`Kitchen display`, icon: ChefHat },
    { to: "/menu", label: msg`Menu`, icon: UtensilsCrossed },
    { to: "/inventory", label: msg`Inventory`, icon: Boxes },
    { to: "/purchasing", label: msg`Purchasing`, icon: Truck },
    { to: "/reports", label: msg`Reports`, icon: BarChart3 },
    { to: "/staff", label: msg`Staff`, icon: Contact },
    { to: "/customers", label: msg`Customers`, icon: Heart },
    { to: "/reservations", label: msg`Reservations`, icon: CalendarClock },
    { to: "/floor", label: msg`Floor`, icon: LayoutGrid },
    { to: "/cashbox", label: msg`Cashbox`, icon: CreditCard },
    // ACTIVITY-CORE: feed L3 (antifraude) + papelera L2 (catálogo)
    { to: "/activity", label: msg`Activity`, icon: HistoryIcon },
    { to: "/achievements", label: msg`Achievements`, icon: Trophy },
    { to: "/trash", label: msg`Trash`, icon: Trash2 },
  ];

  const ADMIN_ITEMS = [
    { to: "/admin", label: msg`Overview`, icon: Shield },
    { to: "/admin/metrics", label: msg`Metrics`, icon: BarChart3 },
    { to: "/admin/users", label: msg`Users`, icon: Users },
    { to: "/admin/audit", label: msg`Audit Log`, icon: Scan },
    { to: "/admin/flags", label: msg`Feature Flags`, icon: Flag },
    { to: "/admin/integrations", label: msg`Integrations`, icon: Plug },
    { to: "/organizations", label: msg`Organizations`, icon: Users },
    { to: "/invitations", label: msg`Invitations`, icon: Mail },
    { to: "/admin/storage", label: msg`Storage`, icon: Shield },
    { to: "/admin/themes", label: msg`Themes`, icon: Palette },
    { to: "/admin/theme-audit", label: msg`Theme Audit`, icon: ShieldAlert },
    { to: "/admin/fonts", label: msg`Fonts`, icon: Type },
  ];

  const handleSignOut = () => {
    props.onCloseSidebar?.();
    promptSignOut();
  };

  return (
    <div class="flex flex-col h-full pt-[var(--safe-area-inset-top)] pb-[var(--safe-area-inset-bottom)] pl-[var(--safe-area-inset-left)]">
      <div class="flex h-16 items-center px-6 shrink-0">
        <Logo size="sm" />
      </div>

      <Separator />

      <nav
        aria-label={t(msg`Main navigation`)}
        class="flex flex-1 flex-col gap-0.5 overflow-y-auto p-2 scrollbar-hide"
      >
        <For each={NAV_ITEMS}>
          {(item) => (
            <SidebarNavItem
              to={item.to}
              label={t(item.label)}
              icon={item.icon}
              exact={item.to === "/"}
              onClick={() => props.onCloseSidebar?.()}
            />
          )}
        </For>

        <Show when={isAdminCapable(session.data?.user.role)}>
          <div class="mt-4 px-3 mb-1">
            <h3 class="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {t(msg`Administration`)}
            </h3>
          </div>
          <For each={ADMIN_ITEMS}>
            {(item) => (
              <SidebarNavItem
                to={item.to}
                label={t(item.label)}
                icon={item.icon}
                onClick={() => props.onCloseSidebar?.()}
              />
            )}
          </For>
        </Show>
      </nav>

      <Separator />

      {/* Bottom actions */}
      <div class="flex flex-col gap-0.5 p-2 shrink-0">
        <SidebarNavItem
          to="/profile"
          label={t(msg`Profile`)}
          icon={User}
          onClick={() => props.onCloseSidebar?.()}
        />
        <SidebarNavItem
          to="/debug"
          label={t(msg`Debug`)}
          icon={Bug}
          onClick={() => props.onCloseSidebar?.()}
        />

        <Show
          when={session.isAuthenticated()}
          fallback={
            <Link
              to="/login"
              search={{ redirect: safeRedirectTarget(location().pathname) }}
              onClick={() => props.onCloseSidebar?.()}
              class={cn(
                "mt-1 flex items-center gap-2.5 rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
                "bg-primary/10 text-primary hover:bg-primary/20",
              )}
            >
              <User class="h-4 w-4" />
              {t(msg`Sign In`)}
            </Link>
          }
        >
          <Button
            variant="ghost"
            class={`mt-1 w-full justify-start gap-2.5 px-3 py-2.5 ${inactiveClass} hover:text-destructive`}
            onClick={handleSignOut}
          >
            <LogOut class="h-4 w-4" />
            {t(msg`Sign Out`)}
          </Button>
        </Show>

        {/* ── Account switcher (multi-account #18) ────────────────────────── */}
        <Separator class="my-1" />
        <button
          type="button"
          onClick={() => {
            props.onCloseSidebar?.();
            accountSwitcher.open();
          }}
          class={cn(
            "flex w-full items-center gap-2.5 rounded-md px-3 py-2.5 text-sm transition-colors",
            inactiveClass,
          )}
          aria-label={t(msg`Switch account`)}
          data-testid="account-switcher-trigger"
        >
          <span class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground text-xs font-bold">
            <Show when={activeAccount()?.kind === "real"} fallback={<UserCircle class="h-4 w-4" />}>
              <span class="uppercase">{activeInitial()}</span>
            </Show>
          </span>
          <span class="min-w-0 flex-1 text-left">
            <span class="block truncate font-medium text-foreground">{activeLabel()}</span>
            <Show when={activeAccount()?.email}>
              <span class="block truncate text-xs text-muted-foreground/80">
                {activeAccount()?.email}
              </span>
            </Show>
          </span>
          <ChevronsUpDown class="h-4 w-4 shrink-0 opacity-60" />
        </button>
      </div>
    </div>
  );
}
